import {createSlice} from '@reduxjs/toolkit'
import {
  fetchProductGroups,
  createProductGroup,
  deleteProductGroup,
  addPlanCodeThunk,
  updatePlanCodeThunk,
  deletePlanCodeThunk,
  fetchServiceIdRules,
  createServiceIdRule,
  updateServiceIdRuleThunk,
  deleteServiceIdRuleThunk,
} from './productRulesThunk'

const initialState = {
  productGroups: [],
  serviceIdRules: [],
  loading: false,
  error: null,
}

const productRulesSlice = createSlice({
  name: 'productRules',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // PRODUCT GROUPS
      .addCase(fetchProductGroups.fulfilled, (state, action) => {
        state.loading = false
        // OOE-UI-EM: an empty/204 response fulfils with undefined and
        // spreading it threw "payload is not iterable" inside the reducer,
        // which left `loading` stuck true and killed the dispatch chain.
        const payload = Array.isArray(action.payload) ? action.payload : []
        state.productGroups = [...payload].sort((a, b) =>
          (a.product_group || '').localeCompare(b.product_group || ''),
        )
      })

      .addCase(createProductGroup.fulfilled, (state, action) => {
        state.loading = false
        const {name, planCodes} = action.payload
        state.productGroups.push(
          ...planCodes.map(code => ({
            product_group: name,
            plan_code: code,
          })),
        )
        state.productGroups.sort((a, b) =>
          a.product_group.localeCompare(b.product_group),
        )
      })

      .addCase(deleteProductGroup.fulfilled, (state, action) => {
        state.loading = false
        state.productGroups = state.productGroups.filter(
          p => p.product_group !== action.payload,
        )
      })

      .addCase(addPlanCodeThunk.fulfilled, (state, action) => {
        state.loading = false
        state.productGroups.push({
          product_group: action.payload.group,
          plan_code: action.payload.code,
        })
      })

      .addCase(updatePlanCodeThunk.fulfilled, (state, action) => {
        state.loading = false
        const {groupName, oldCode, newCode} = action.payload
        const item = state.productGroups.find(
          p => p.product_group === groupName && p.plan_code === oldCode,
        )
        if (item) item.plan_code = newCode
      })

      .addCase(deletePlanCodeThunk.fulfilled, (state, action) => {
        state.loading = false
        state.productGroups = state.productGroups.filter(
          p =>
            !(
              p.product_group === action.payload.groupName &&
              p.plan_code === action.payload.code
            ),
        )
      })

      // SERVICE ID RULES
      .addCase(fetchServiceIdRules.pending, state => {
        state.loading = true
        state.error = null
        state.serviceIdRules = []
      })
      .addCase(fetchServiceIdRules.fulfilled, (state, action) => {
        state.loading = false
        state.serviceIdRules = action.payload
      })

      .addCase(createServiceIdRule.fulfilled, (state, action) => {
        state.loading = false
        state.serviceIdRules.push(action.payload)
      })

      .addCase(updateServiceIdRuleThunk.fulfilled, (state, action) => {
        state.loading = false
        const updated = action.payload
        const index = state.serviceIdRules.findIndex(
          r =>
            r.country === updated.country &&
            r.language === updated.language &&
            r.region === updated.region &&
            r.userRole === updated.userRole &&
            r.callType === updated.callType &&
            JSON.stringify([...r.productGrp].sort()) ===
              JSON.stringify([...updated.productGrp].sort()),
        )
        if (index !== -1) state.serviceIdRules[index] = updated
      })

      .addCase(deleteServiceIdRuleThunk.fulfilled, (state, action) => {
        state.loading = false
        const {
          language,
          country,
          region,
          userRole,
          callType,
          productGrp,
          serviceId,
        } = action.payload
        state.serviceIdRules = state.serviceIdRules.filter(
          r =>
            !(
              r.language === language &&
              r.country === country &&
              r.region === region &&
              r.userRole === userRole &&
              r.callType === callType &&
              r.serviceId === serviceId &&
              JSON.stringify([...r.productGrp].sort()) ===
                JSON.stringify([...productGrp].sort())
            ),
        )
      })

      .addMatcher(
        action =>
          action.type.startsWith('productRules/') &&
          action.type.endsWith('/pending'),
        state => {
          state.loading = true
          state.error = null
        },
      )

      .addMatcher(
        action =>
          action.type.startsWith('productRules/') &&
          action.type.endsWith('/rejected'),
        (state, action) => {
          state.loading = false
          state.error = action.payload
        },
      )
  },
})

export const selectProductGroups = state => state.productRules.productGroups
export const selectServiceIdRules = state => state.productRules.serviceIdRules
export const selectLoading = state => state.productRules.loading
export const selectError = state => state.productRules.error
export const selectAddonCodeMap = state => {
  const groups = state.productRules.productGroups
  return groups.reduce((map, {product_group, plan_code}) => {
    if (!map[product_group]) map[product_group] = []
    map[product_group].push(plan_code)
    return map
  }, {})
}

export default productRulesSlice.reducer
